import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import {
  Button,
  Input,
  Card,
  AuthLayout,
  Link,
  Logo,
  AuthFooter,
} from "@accredo/fe-shared/theme/components/index.js";
import { colorsSemantic } from "@accredo/fe-shared/theme/tokens/colors-semantic.js";
import {
  dimensions,
  widths,
} from "@accredo/fe-shared/theme/tokens/dimensions.js";
import LoginIcon from "@mui/icons-material/Login";
import UserContext from "@accredo/fe-shared/contexts/UserContext.js";
import {
  requestLogin,
  requestPasswordReset,
  resetPassword,
  getResetTokenEmail,
} from "@accredo/fe-shared/services/dataService.js";
import {
  processLoginSuccess,
  readRedirectUrlOnce,
} from "@accredo/fe-shared/services/authService.js";
import { resolveLoginMessage } from "@accredo/fe-shared/services/loginHelpers.js";
import TwoFactorFlow from "../components/auth/TwoFactorFlow.jsx";
import PasswordResetForm from "../components/auth/PasswordResetForm.jsx";
import EnvironmentBanner from "../components/EnvironmentBanner.jsx";
import MaintenanceNotice from "../components/MaintenanceNotice.jsx";

/**
 * Login Page, shared by every portal. Matches the Figma design with accredo
 * branding; which portal it is only shows up in the title and in where it
 * lands the user after signing in.
 *
 * @param {string} [appTitle] - Title shown under the logo.
 * @param {string} [homePath] - Where to land after login (or an already
 *   signed-in visit) when nothing sent the user to the login first.
 */
export default function LoginPage({
  appTitle = "Schadenportal",
  homePath = "/home",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const userContext = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login");
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [secToken, setSecToken] = useState("");
  const [isNewAccount, setIsNewAccount] = useState(false);

  // Redirects once a user is present in context - both for a visitor who
  // already has a session (e.g. opening /login directly while signed in) and
  // right after a login on this page sets it. This is the only place that
  // navigates away after sign-in; the login handler below only sets the
  // user.
  useEffect(() => {
    if (userContext?.user) {
      navigate(readRedirectUrlOnce() ?? homePath);
    }
  }, [userContext?.user, navigate, homePath]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token") || "";
    const action = searchParams.get("action");
    const emailParam = searchParams.get("email");
    if (token) {
      setSecToken(token);
    }
    if (emailParam) {
      setEmail(emailParam);
    }
    setIsNewAccount(searchParams.get("new_account") === "1");

    if (token) {
      setView("setNew");
    } else if (action === "reset-password") {
      setView("sendLink");
    }
  }, [location.search]);

  useEffect(() => {
    if (!secToken) return;

    getResetTokenEmail(secToken, {
      onSuccess: (response) => {
        const resolvedEmail = response.data?.data?.email;
        if (resolvedEmail) {
          setEmail(resolvedEmail);
        }
      },
    });
  }, [secToken]);

  const showResponse = ({
    error = null,
    response = null,
    variant = "error",
    fallback = null,
  }) => {
    const message = resolveLoginMessage({ error, response, fallback });
    enqueueSnackbar(message, { variant });
    if (error) {
      console.error("[LOGIN] Error response", error);
    }
  };

  const handleSetUser = (user) => {
    if (userContext && typeof userContext.setUser === "function") {
      userContext.setUser(user);
    }
  };

  const processLoginData = async (data, fallbackMessage) => {
    try {
      const user = await processLoginSuccess(data);
      if (user) {
        handleSetUser(user);
        return true;
      }
      showResponse({ fallback: fallbackMessage });
    } catch (error) {
      showResponse({ error });
    }
    return false;
  };

  const handleTwoFactorSuccess = async (verificationData) => {
    try {
      return await processLoginData(
        verificationData,
        "Fehler beim Abrufen der Benutzerdaten nach der 2FA-Verifizierung."
      );
    } finally {
      setLoading(false);
      setView("login");
      setTwoFactorData(null);
      setPassword("");
    }
  };

  const handleTwoFactorCancel = () => {
    setView("login");
    setTwoFactorData(null);
    setPassword("");
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log("[LOGIN] Attempting login", { email });

    await requestLogin(email, password, {
      setIsLoading: setLoading,
      onSuccess: async (data) => {
        await processLoginData(
          data,
          "Fehler beim Abrufen der Benutzerdaten nach dem Login."
        );
      },
      onError: (error) => {
        showResponse({ error });
      },
      onTwoFactorRequired: (data) => {
        setTwoFactorData(data);
        setView("twoFactor");
        showResponse({
          response: { data },
          variant: "info",
          fallback: "Zwei-Faktor-Authentifizierung erforderlich.",
        });
      },
    });
  };

  const handleForgotPassword = () => {
    setView("sendLink");
    setTwoFactorData(null);
  };

  const handleBackToLogin = () => {
    setView("login");
    setTwoFactorData(null);
    setLoading(false);
  };

  const handleSendResetRequest = async () => {
    if (!email) {
      showResponse({ fallback: "Bitte geben Sie Ihre E-Mail-Adresse ein." });
      return;
    }

    await requestPasswordReset(email, {
      setIsLoading: setLoading,
      onSuccess: (response) => {
        enqueueSnackbar("Kontrollieren Sie Ihren Posteingang.", {
          variant: "success",
        });
        setView("afterResetRequest");
      },
      onError: (error) => {
        showResponse({ error });
      },
    });
  };

  const handleResetSubmit = async (
    { token, password: newPassword },
    formMethods
  ) => {
    let success = false;
    await resetPassword(token, newPassword, {
      setIsLoading: setLoading,
      setError: formMethods?.setError,
      enqueueSnackbar,
      successMessage: "Passwort wurde erfolgreich gesetzt.",
      onSuccess: async (response) => {
        if (response.status === 200) {
          const loginData = response.data?.data;
          const loginSuccess = await processLoginData(
            loginData,
            "Fehler beim Abrufen der Benutzerdaten nach dem Login."
          );
          success = Boolean(loginSuccess);
        } else {
          showResponse({
            response,
            fallback: "Fehler beim Zurücksetzen des Passworts.",
          });
        }
      },
      onError: (error) => {
        showResponse({ error });
      },
    });

    if (success) {
      setView("login");
      setPassword("");
      setSecToken("");
    }

    return success;
  };

  const renderLoginForm = () => (
    <Box
      component="form"
      onSubmit={handleLogin}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: dimensions["3xl"],
      }}
    >
      <Input
        label="E-Mail-Adresse"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        autoComplete="username"
      />

      <Input
        label="Passwort"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        autoComplete="current-password"
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: { xs: "wrap-reverse", sm: "nowrap" },
          gap: dimensions["xl"],
          columnGap: dimensions["xl"],
        }}
      >
        <Link
          variant="primary"
          onClick={handleForgotPassword}
          underline="none"
          sx={{
            px: dimensions["lg"],
            py: dimensions["md"],
          }}
        >
          Passwort vergessen?
        </Link>

        <Button
          variant="primary"
          type="submit"
          size="large"
          disabled={loading}
          endIcon={!loading && <LoginIcon fontSize="small" />}
        >
          {loading ? "Wird geladen..." : "Anmelden"}
        </Button>
      </Box>
    </Box>
  );

  const renderSendLink = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: dimensions["2xl"] }}
    >
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: dimensions["md"],
        }}
      >
        <Typography variant="h6">
          {isNewAccount ? "Zugang aktivieren" : "Passwort zurücksetzen"}
        </Typography>
        <Typography color="text.secondary">
          {isNewAccount
            ? 'Klicken Sie auf "Zugang aktivieren", um einen Link zum Festlegen Ihres Passworts zu erhalten.'
            : "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts."}
        </Typography>
      </Box>

      <Input
        label="E-Mail-Adresse"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        autoComplete="username"
      />

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: dimensions["lg"] }}
      >
        <Button
          variant="primary"
          size="large"
          onClick={handleSendResetRequest}
          disabled={loading}
        >
          {loading
            ? "Wird gesendet..."
            : isNewAccount
              ? "Zugang aktivieren"
              : "Zurücksetzlink senden"}
        </Button>
        {!isNewAccount && (
          <Button variant="tertiary" onClick={handleBackToLogin}>
            Zurück zur Anmeldung
          </Button>
        )}
      </Box>
    </Box>
  );

  const renderAfterResetRequest = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: dimensions["2xl"],
        textAlign: "center",
      }}
    >
      <Typography>
        Wir haben Ihnen eine E-Mail mit einem Link zum{" "}
        {isNewAccount ? "Setzen" : "Zurücksetzen"} Ihres Passworts gesendet.
        Bitte überprüfen Sie Ihren Posteingang und folgen Sie den Anweisungen.
      </Typography>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: dimensions["lg"] }}
      >
        {!isNewAccount && (
          <Button variant="primary" onClick={handleBackToLogin}>
            Zur Anmeldung
          </Button>
        )}
        <Button variant="tertiary" onClick={() => setView("sendLink")}>
          Neuen Link anfordern
        </Button>
      </Box>
    </Box>
  );

  const renderSetNewPassword = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: dimensions["2xl"] }}
    >
      <Typography variant="h6" align="center">
        Neues Passwort festlegen
      </Typography>
      <PasswordResetForm
        token={secToken}
        email={email}
        onSubmit={async (data, formMethods) =>
          handleResetSubmit(data, formMethods)
        }
        isLoading={loading}
      />
      <Button variant="tertiary" onClick={handleBackToLogin}>
        Zurück zur Anmeldung
      </Button>
    </Box>
  );

  const renderContent = () => {
    if (view === "twoFactor" && twoFactorData) {
      return (
        <TwoFactorFlow
          email={email}
          challengeToken={twoFactorData.challenge_token}
          onSuccess={handleTwoFactorSuccess}
          onCancel={handleTwoFactorCancel}
          initialChallengeData={twoFactorData.challenge_data}
        />
      );
    }

    switch (view) {
      case "sendLink":
        return renderSendLink();
      case "afterResetRequest":
        return renderAfterResetRequest();
      case "setNew":
        return renderSetNewPassword();
      case "login":
      default:
        return renderLoginForm();
    }
  };

  return (
    <AuthLayout>
      <Card
        variant="auth"
        sx={{
          width: "100%",
          maxWidth: widths.sm,
          margin: dimensions["3xl"],
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        <EnvironmentBanner short />
        <MaintenanceNotice sx={{ borderRadius: 0 }} />

        {/* padded Card content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: dimensions["3xl"],
            padding: dimensions["4xl"],
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: dimensions["lg"],
            }}
          >
            <Logo size="medium" center />

            <Typography
              variant="pageTitle"
              color={colorsSemantic.text.accent.blue}
              align="center"
            >
              {appTitle}
            </Typography>
          </Box>

          {renderContent()}
        </Box>
      </Card>
      <AuthFooter />
    </AuthLayout>
  );
}

LoginPage.propTypes = {
  appTitle: PropTypes.string,
  homePath: PropTypes.string,
};
